import { DefaultValues, useForm, UseFormResetField } from 'react-hook-form';
import { ZodEffects, ZodObject, ZodRawShape } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@casops/store/hooks';
import type { TagDescription } from '@reduxjs/toolkit/query';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ApiBaseQueryError,
  ApiBaseQueryFn,
  casOpsApi,
  ModelName,
  QueryTag,
} from '@casops/store/api';
import { ModelFormControlProps } from './ModelForm';
import { ModelWithId } from '@casops/store/apis/commonModels';
import type { TypedMutationTrigger } from '@reduxjs/toolkit/query/react';
import { useFormEditMode } from './useFormEditMode';
import {
  ErrorResponse,
  SuccessNotificationContext,
  handleFieldErrors,
  ServerErrorFields,
} from '@dmc/ui';

export interface CanSubmitProps {
  isSubmitting: boolean;
  isAnyFieldDirty: boolean;
  isValid: boolean;
  editMode: boolean;
}

export const canSubmitDefault = ({
  isSubmitting,
  isAnyFieldDirty,
  isValid,
}: CanSubmitProps): boolean => {
  return !isSubmitting && isAnyFieldDirty && isValid;
};

interface UseModelFormProps<
  TDataModel extends ModelWithId & TEditModel,
  TFormModel extends TCreateModel & TEditModel,
  TCreateModel extends object = TFormModel,
  TEditModel extends object = TFormModel,
> {
  modelName: ModelName;
  defaultValues: DefaultValues<TFormModel>;
  item?: TDataModel;
  schema: ZodEffects<ZodObject<ZodRawShape>> | ZodObject<ZodRawShape>;
  triggerCreateItem?: TypedMutationTrigger<void, TCreateModel, ApiBaseQueryFn>; // optional
  getCreateArgs?: (values: TFormModel) => TCreateModel;
  triggerEditItem?: TypedMutationTrigger<void, TEditModel & ModelWithId, ApiBaseQueryFn>;
  getEditArgs?: (values: TFormModel) => TEditModel;
  serverErrorFields: ServerErrorFields<TFormModel>;
  canSubmitCallback?: (props: CanSubmitProps) => boolean;
  validateOnLoad?: boolean;
  editMode?: boolean; // Allow the edit mode to be specified directly rather than relying on side panel global state.
  customSuccessCallback?: () => void;
  tagsToClearOnSuccess?: TagDescription<QueryTag>[];
}

interface UseModelFormReturn<TFormModel extends object> extends ModelFormControlProps<TFormModel> {
  resetField: UseFormResetField<TFormModel>;
  isAnyFieldDirty: boolean;
}

export const useModelForm = <
  TDataModel extends ModelWithId & TEditModel,
  TFormModel extends TCreateModel & TEditModel,
  TCreateModel extends object = TFormModel,
  TEditModel extends object = TFormModel,
>({
  modelName,
  defaultValues,
  item,
  schema,
  triggerCreateItem,
  getCreateArgs,
  triggerEditItem,
  getEditArgs,
  serverErrorFields,
  canSubmitCallback = canSubmitDefault,
  validateOnLoad = false,
  editMode: editModeOverride,
  customSuccessCallback,
  tagsToClearOnSuccess,
}: UseModelFormProps<
  TDataModel,
  TFormModel,
  TCreateModel,
  TEditModel
>): UseModelFormReturn<TFormModel> => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const editMode = useFormEditMode(editModeOverride);

  // Override the default values with the item data if it exists, otherwise use the default values.
  const initialValues = useMemo<DefaultValues<TFormModel>>(() => {
    return item && editMode ? { ...defaultValues, ...item } : defaultValues;
  }, [defaultValues, item, editMode]);

  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting, isValid, dirtyFields },
    resetField,
    trigger,
  } = useForm<TFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // Add useEffect to trigger validation when item data loads
  useEffect(() => {
    if (item && editMode && validateOnLoad) {
      trigger(); // Trigger validation for all fields
    }
  }, [item, editMode, trigger, validateOnLoad]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;

  // The canSubmitCallback allows the parent to determine if the form can be submitted based on model-specific logic.
  const canSubmit = canSubmitCallback({
    isSubmitting,
    isAnyFieldDirty,
    isValid,
    editMode,
  });

  const successCallback = useCallback((): void => {
    tagsToClearOnSuccess && dispatch(casOpsApi.util.invalidateTags(tagsToClearOnSuccess));
    if (customSuccessCallback) {
      customSuccessCallback();
    } else {
      dispatch(closeSidePanel());
    }
  }, [customSuccessCallback, tagsToClearOnSuccess, dispatch]);

  const onSubmitInternal = useCallback(
    async (values: TFormModel) => {
      try {
        setErrorMessage(undefined);

        if (editMode && typeof triggerEditItem !== 'undefined') {
          if (typeof item?.id !== 'undefined') {
            // Optionally run a callback to get the mutation args from the form.
            const args: TEditModel = getEditArgs ? getEditArgs(values) : values;
            await triggerEditItem({ ...args, id: item.id }).unwrap();
          } else {
            // This error message should never be reached, but it is here for safety.
            setErrorMessage(t('Model.Edit.Error', { modelName }));
          }
        } else if (!editMode && typeof triggerCreateItem !== 'undefined') {
          // Optionally run a callback to get the mutation args from the form.
          const args: TCreateModel = getCreateArgs ? getCreateArgs(values) : values;
          await triggerCreateItem({ ...args }).unwrap();
        } else {
          // Handle the scenario where the component has not been provided the required props
          // to perform the action of the current side panel mode.
          setErrorMessage(t('UnexpectedErrorOccurred'));
        }

        // If this point is reached, the create/edit action was successful, so show success message and close the side panel.
        showSuccessMessage(t(`Model.${editMode ? 'Edit' : 'Create'}.Success`, { modelName }));
        successCallback();
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        const validationError = errorResponse.getValidationError();
        if (validationError) {
          handleFieldErrors<TFormModel>({
            serverErrorFields,
            validationError,
            setError,
          });
        }
        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [
      successCallback,
      showSuccessMessage,
      triggerEditItem,
      triggerCreateItem,
      setError,
      getCreateArgs,
      getEditArgs,
      serverErrorFields,
      editMode,
      modelName,
      item?.id,
      t,
    ],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  return {
    canSubmit,
    onSubmit,
    errorMessage,
    control,
    resetField,
    isAnyFieldDirty,
  };
};

export default useModelForm;
